import React, {useState, useEffect, useRef, useCallback} from 'react';
import * as serviceWorkerRegistration from '../serviceWorkerRegistration';
import {IonButton, IonIcon} from "@ionic/react";
import {downloadOutline} from "ionicons/icons";
import {App} from "@capacitor/app";
import type {PluginListenerHandle} from '@capacitor/core';

const SoftwareUpdateIndicatorButton: React.FC = () => {
    const [showReload, setShowReload] = useState(false);
    const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
    const updateIntervalRef = useRef<number | null>(null);
    const appStateListenerRef = useRef<PluginListenerHandle | null>(null);

    const onSWUpdate = useCallback((registration: ServiceWorkerRegistration) => {
        setShowReload(true);
        setWaitingWorker(registration.waiting);
    }, []);

    useEffect(() => {
        if(!navigator.serviceWorker) {
            console.log('<SoftwareUpdateIndicatorButton> No service worker support available');
            return;
        }

        let disposed = false;

        const checkForUpdate = async (registration: ServiceWorkerRegistration, reason: string) => {
            try {
                console.debug(`<SoftwareUpdateIndicatorButton> Checking for updates (${reason}).`);
                await registration.update();
            } catch (error) {
                console.warn('<SoftwareUpdateIndicatorButton> Update check failed', error);
            }
        };

        void navigator.serviceWorker.ready.then(async registration => {
            if (disposed) {
                return;
            }

            const listener = await App.addListener('appStateChange', async ({isActive}) => {
                console.log('App state changed. isActive', isActive);

                if(isActive) {
                    await checkForUpdate(registration, 'foreground');
                }
            });

            if (disposed) {
                await listener.remove();
                return;
            }

            appStateListenerRef.current = listener;
            updateIntervalRef.current = window.setInterval(() => {
                void checkForUpdate(registration, 'periodic');
            }, 1000 * 60 * 5);
        }).catch(error => {
            console.warn('<SoftwareUpdateIndicatorButton> Unable to monitor for updates', error);
        });

        serviceWorkerRegistration.register({ onUpdate: onSWUpdate });

        return () => {
            disposed = true;

            if (updateIntervalRef.current !== null) {
                window.clearInterval(updateIntervalRef.current);
                updateIntervalRef.current = null;
            }

            const listener = appStateListenerRef.current;
            appStateListenerRef.current = null;
            void listener?.remove();
        };
    }, [onSWUpdate]);

    const reloadPage = () => {
        waitingWorker?.postMessage({ type: 'SKIP_WAITING' });
        setShowReload(false);
        window.location.reload();
    };

    return (showReload ? <IonButton onClick={reloadPage} color="warning">
        <IonIcon slot="icon-only" icon={downloadOutline} />
    </IonButton> : null);
};

export default SoftwareUpdateIndicatorButton;
